import { useCallback, useEffect, useMemo, useState } from 'react'
import { EmptyState } from '../components/EmptyState'
import { InlineNotice } from '../components/InlineNotice'
import { LoadingBlock } from '../components/LoadingBlock'
import { Modal } from '../components/Modal'
import { StatusBadge } from '../components/StatusBadge'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { clientDisplayName, formatDate, formatMoney, leadStatusLabel, leadStatusOptions, leadStatusTone, serviceName, sourceName } from '../lib/format'
import { supabase } from '../lib/supabase'
import { errorMessage } from '../lib/errors'
import { consumePendingAction } from '../lib/pendingAction'
import type { ClientRow, LeadRow, LeadStatus, SimpleProfile, SimpleService, SimpleSource } from '../lib/types'

type ClientMode = 'existing' | 'contact' | 'new'
type LeadForm = {
  client_mode: ClientMode
  client_id: string
  contact_label: string
  new_client_type: 'individual' | 'company'
  new_client_first_name: string
  new_client_last_name: string
  new_client_company_name: string
  new_client_email: string
  new_client_phone: string
  new_client_instagram: string
  source_id: string
  service_id: string
  desired_date: string
  responsible_user_id: string
  status: LeadStatus
  estimated_budget_gross: string
  occasion: string
  lost_reason: string
  notes: string
}

type OrderForm = {
  fulfilment_at: string
  amount_gross: string
  delivery_method: 'pickup' | 'delivery' | 'onsite'
  delivery_address: string
}

const tomorrow = () => {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  return date.toISOString().slice(0, 10)
}

const emptyLeadForm = (): LeadForm => ({
  client_mode: 'existing',
  client_id: '',
  contact_label: '',
  new_client_type: 'individual',
  new_client_first_name: '',
  new_client_last_name: '',
  new_client_company_name: '',
  new_client_email: '',
  new_client_phone: '',
  new_client_instagram: '',
  source_id: '',
  service_id: '',
  desired_date: tomorrow(),
  responsible_user_id: '',
  status: 'new',
  estimated_budget_gross: '',
  occasion: '',
  lost_reason: '',
  notes: ''
})

function leadClientName(lead: LeadRow) {
  return lead.clients ? clientDisplayName(lead.clients) : lead.contact_label || '—'
}

export function LeadsPage() {
  const { t, language } = useLanguage()
  const { user, profile } = useAuth()
  const [leads, setLeads] = useState<LeadRow[]>([])
  const [clients, setClients] = useState<ClientRow[]>([])
  const [sources, setSources] = useState<SimpleSource[]>([])
  const [services, setServices] = useState<SimpleService[]>([])
  const [profiles, setProfiles] = useState<SimpleProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | LeadStatus>('all')
  const [sourceFilter, setSourceFilter] = useState('all')
  const [responsibleFilter, setResponsibleFilter] = useState('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<LeadRow | null>(null)
  const [form, setForm] = useState<LeadForm>(emptyLeadForm())
  const [saving, setSaving] = useState(false)
  const [orderOpen, setOrderOpen] = useState(false)
  const [orderSaving, setOrderSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [orderForm, setOrderForm] = useState<OrderForm>({ fulfilment_at: '', amount_gross: '', delivery_method: 'pickup', delivery_address: '' })

  const load = useCallback(async () => {
    if (!supabase) return
    setLoading(true)
    setError(null)
    const [leadResult, clientResult, sourceResult, serviceResult, profileResult] = await Promise.all([
      supabase.from('leads').select(`
        *,
        clients(id,client_type,first_name,last_name,company_name,instagram_handle,email,phone),
        lead_sources(id,code,name_pl,name_ru),
        services(id,code,name_pl,name_ru),
        responsible:profiles!leads_responsible_user_id_fkey(id,full_name,email,role),
        orders(id,number)
      `).order('created_at', { ascending: false }),
      supabase.from('clients').select('*').eq('active', true).order('updated_at', { ascending: false }),
      supabase.from('lead_sources').select('id,code,name_pl,name_ru').eq('active', true).order('name_pl'),
      supabase.from('services').select('id,code,name_pl,name_ru,kind,active').eq('active', true).eq('kind', 'primary').order('name_pl'),
      supabase.from('profiles').select('id,full_name,email,role,active').eq('active', true).order('full_name')
    ])
    const firstError = leadResult.error || clientResult.error || sourceResult.error || serviceResult.error || profileResult.error
    if (firstError) {
      setError(firstError.message)
    } else {
      setLeads((leadResult.data || []) as unknown as LeadRow[])
      setClients((clientResult.data || []) as ClientRow[])
      setSources((sourceResult.data || []) as SimpleSource[])
      setServices((serviceResult.data || []) as SimpleService[])
      setProfiles((profileResult.data || []) as SimpleProfile[])
    }
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    const open = () => openCreate()
    if (consumePendingAction('new-lead')) openCreate()
    window.addEventListener('sgflow:new-lead', open)
    return () => window.removeEventListener('sgflow:new-lead', open)
  }, [profiles.length])

  const canDelete = Boolean(profile?.is_system_admin || profile?.role === 'owner' || profile?.role === 'manager')

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return leads.filter((lead) => {
      if (statusFilter !== 'all' && lead.status !== statusFilter) return false
      if (sourceFilter !== 'all' && lead.source_id !== sourceFilter) return false
      if (responsibleFilter !== 'all' && lead.responsible_user_id !== responsibleFilter) return false
      if (!query) return true
      const haystack = [lead.number, leadClientName(lead), lead.occasion, lead.notes, lead.services?.name_pl, lead.services?.name_ru].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(query)
    })
  }, [leads, responsibleFilter, search, sourceFilter, statusFilter])

  function openCreate() {
    const next = emptyLeadForm()
    if (profiles.length === 1) next.responsible_user_id = profiles[0].id
    setSelected(null)
    setForm(next)
    setError(null)
    setNotice(null)
    setModalOpen(true)
  }

  function openEdit(lead: LeadRow) {
    setSelected(lead)
    setForm({
      client_mode: lead.client_id ? 'existing' : 'contact',
      client_id: lead.client_id || '',
      contact_label: lead.contact_label || '',
      new_client_type: 'individual',
      new_client_first_name: '',
      new_client_last_name: '',
      new_client_company_name: '',
      new_client_email: '',
      new_client_phone: '',
      new_client_instagram: '',
      source_id: lead.source_id,
      service_id: lead.service_id,
      desired_date: lead.desired_date,
      responsible_user_id: lead.responsible_user_id,
      status: lead.status,
      estimated_budget_gross: lead.estimated_budget_gross == null ? '' : String(lead.estimated_budget_gross),
      occasion: lead.occasion || '',
      lost_reason: lead.lost_reason || '',
      notes: lead.notes || ''
    })
    setError(null)
    setNotice(null)
    setModalOpen(true)
  }

  function update<K extends keyof LeadForm>(key: K, value: LeadForm[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }


  async function save() {
    if (!supabase || !user) return
    if (!form.source_id || !form.service_id || !form.desired_date || !form.responsible_user_id) {
      setError(t('requiredFields'))
      return
    }
    if (form.status === 'lost' && !form.lost_reason.trim()) {
      setError(t('lostReason'))
      return
    }
    if (form.client_mode === 'existing' && !form.client_id) {
      setError(t('chooseClient'))
      return
    }
    if (form.client_mode === 'contact' && !form.contact_label.trim()) {
      setError(t('contactLabel'))
      return
    }
    if (form.client_mode === 'new') {
      const hasNewIdentity = form.new_client_type === 'company'
        ? form.new_client_company_name.trim()
        : form.new_client_first_name.trim() || form.new_client_instagram.trim() || form.new_client_email.trim() || form.new_client_phone.trim()
      if (!hasNewIdentity) {
        setError(t('clientName'))
        return
      }
    }

    setSaving(true)
    setError(null)
    try {
      const payload = {
        client_id: form.client_mode === 'existing' ? form.client_id : null,
        contact_label: form.client_mode === 'contact' ? form.contact_label.trim() : null,
        source_id: form.source_id,
        service_id: form.service_id,
        desired_date: form.desired_date,
        responsible_user_id: form.responsible_user_id,
        status: form.status,
        estimated_budget_gross: form.estimated_budget_gross ? Number(form.estimated_budget_gross.replace(',', '.')) : null,
        occasion: form.occasion.trim() || null,
        lost_reason: form.status === 'lost' ? form.lost_reason.trim() : null,
        notes: form.notes.trim() || null
      }
      if (selected) {
        const result = await supabase.from('leads').update(payload).eq('id', selected.id)
        if (result.error) throw result.error
      } else {
        const { error: rpcError } = await supabase.rpc('create_lead_v2', {
          p_payload: {
            client_mode: form.client_mode,
            client_id: form.client_mode === 'existing' ? form.client_id : null,
            contact_label: form.client_mode === 'contact' ? form.contact_label.trim() : null,
            new_client_type: form.client_mode === 'new' ? form.new_client_type : null,
            new_client_first_name: form.client_mode === 'new' ? form.new_client_first_name.trim() || null : null,
            new_client_last_name: form.client_mode === 'new' ? form.new_client_last_name.trim() || null : null,
            new_client_company_name: form.client_mode === 'new' ? form.new_client_company_name.trim() || null : null,
            new_client_email: form.client_mode === 'new' ? form.new_client_email.trim() || null : null,
            new_client_phone: form.client_mode === 'new' ? form.new_client_phone.trim() || null : null,
            new_client_instagram: form.client_mode === 'new' ? form.new_client_instagram.trim() || null : null,
            source_id: form.source_id,
            service_id: form.service_id,
            desired_date: form.desired_date,
            responsible_user_id: form.responsible_user_id,
            status: form.status,
            estimated_budget_gross: form.estimated_budget_gross ? Number(form.estimated_budget_gross.replace(',', '.')) : null,
            occasion: form.occasion.trim() || null,
            lost_reason: form.status === 'lost' ? form.lost_reason.trim() : null,
            notes: form.notes.trim() || null
          }
        })
        if (rpcError) throw rpcError
      }
      setModalOpen(false)
      setNotice(selected ? t('leadUpdated') : t('leadCreated'))
      await load()
    } catch (caught) {
      setError(errorMessage(caught, t('errorSaving')))
    }
    setSaving(false)
  }

  function openOrder(lead: LeadRow) {
    if (!lead.client_id) {
      setNotice(t('clientRequiredForOrder'))
      return
    }
    setSelected(lead)
    const date = new Date(`${lead.desired_date}T09:00:00`)
    const localValue = new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
    setOrderForm({ fulfilment_at: localValue, amount_gross: lead.estimated_budget_gross ? String(lead.estimated_budget_gross) : '', delivery_method: 'pickup', delivery_address: '' })
    setError(null)
    setOrderOpen(true)
  }

  async function createOrder() {
    if (!supabase || !selected || !orderForm.fulfilment_at || !orderForm.amount_gross) return
    if (orderForm.delivery_method === 'delivery' && !orderForm.delivery_address.trim()) {
      setError(t('deliveryAddress'))
      return
    }
    setOrderSaving(true)
    setError(null)
    const { error: rpcError } = await supabase.rpc('create_order_v2', {
      p_payload: {
        lead_id: selected.id,
        fulfilment_at: new Date(orderForm.fulfilment_at).toISOString(),
        amount_gross: Number(orderForm.amount_gross.replace(',', '.')),
        vat_rate: 23,
        delivery_method: orderForm.delivery_method,
        delivery_address: orderForm.delivery_method === 'delivery' ? orderForm.delivery_address.trim() : null,
        confirmation_status: 'confirmed',
        payment_status: 'unpaid',
        fulfilment_status: 'to_plan'
      }
    })
    if (rpcError) {
      setError(rpcError.message)
    } else {
      setOrderOpen(false)
      setModalOpen(false)
      setNotice(t('orderCreated'))
      await load()
    }
    setOrderSaving(false)
  }

  async function deleteLead(lead: LeadRow) {
    if (!supabase || !window.confirm(t('confirmDeleteLead'))) return
    setDeletingId(lead.id)
    setError(null)
    setNotice(null)
    const { error: rpcError } = await supabase.rpc('delete_lead_v1', { p_lead_id: lead.id })
    if (rpcError) {
      setError(errorMessage(rpcError, t('errorSaving')))
    } else {
      setNotice(t('leadDeleted'))
      if (selected?.id === lead.id) {
        setModalOpen(false)
        setSelected(null)
      }
      await load()
    }
    setDeletingId(null)
  }

  return (
    <div className="module-stack">
      <section className="module-toolbar panel toolbar-wrap">
        <div className="toolbar-search"><span>⌕</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('search')} /></div>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as 'all' | LeadStatus)}><option value="all">{t('statusAll')}</option>{leadStatusOptions.map((status) => <option key={status} value={status}>{leadStatusLabel(status, language)}</option>)}</select>
        <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}><option value="all">{t('sourceAll')}</option>{sources.map((source) => <option key={source.id} value={source.id}>{sourceName(source, language)}</option>)}</select>
        <select value={responsibleFilter} onChange={(event) => setResponsibleFilter(event.target.value)}><option value="all">{t('responsibleAll')}</option>{profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.full_name || profile.email}</option>)}</select>
        <button className="secondary-button" type="button" onClick={() => void load()}>{t('refresh')}</button>
        <button className="primary-button" type="button" onClick={openCreate}>＋ {t('createLead')}</button>
      </section>

      {notice && <InlineNotice type={notice === t('clientRequiredForOrder') ? 'info' : 'success'}>{notice}</InlineNotice>}
      {error && !modalOpen && !orderOpen && <InlineNotice type="error">{error}</InlineNotice>}

      {loading ? <LoadingBlock /> : filtered.length === 0 ? (
        leads.length === 0 ? <EmptyState title={t('leadsEmpty')} description={t('leadsEmptyDescription')} action={<button className="primary-button" onClick={openCreate}>{t('createLead')}</button>} /> : <EmptyState title={t('noResults')} description={t('noResultsDescription')} />
      ) : (
        <section className="data-card panel">
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>{t('number')}</th><th>{t('client')}</th><th>{t('service')}</th><th>{t('desiredDate')}</th><th>{t('responsible')}</th><th>{t('status')}</th><th>{t('budgetGross')}</th><th>{t('actions')}</th></tr></thead>
              <tbody>{filtered.map((lead) => <tr key={lead.id}>
                <td><button className="record-title" type="button" onClick={() => openEdit(lead)}>{lead.number}</button><div className="record-subtitle">{sourceName(lead.lead_sources, language)}</div></td>
                <td>{leadClientName(lead)}</td>
                <td>{serviceName(lead.services, language)}</td>
                <td>{formatDate(lead.desired_date, language)}</td>
                <td>{lead.responsible?.full_name || lead.responsible?.email || '—'}</td>
                <td><StatusBadge tone={leadStatusTone(lead.status)}>{leadStatusLabel(lead.status, language)}</StatusBadge></td>
                <td>{lead.estimated_budget_gross == null ? '—' : formatMoney(lead.estimated_budget_gross, language)}</td>
                <td><div className="row-actions"><button className="table-action" type="button" onClick={() => openEdit(lead)}>{t('edit')}</button>{lead.status !== 'lost' && !(lead.orders?.length) && <button className="table-action strong" type="button" onClick={() => openOrder(lead)}>{t('convertToOrder')}</button>}{canDelete && <button className="table-action danger-text" type="button" disabled={deletingId === lead.id} onClick={() => void deleteLead(lead)}>{t('deleteLead')}</button>}</div></td>
              </tr>)}</tbody>
            </table>
          </div>
        </section>
      )}

      <Modal open={modalOpen} title={selected ? t('editLead') : t('createLead')} subtitle={selected ? selected.number : t('requiredFields')} onClose={() => setModalOpen(false)} wide footer={<><button className="secondary-button" onClick={() => setModalOpen(false)}>{t('cancel')}</button>{selected && canDelete && <button className="secondary-button danger-button" disabled={deletingId === selected.id} onClick={() => void deleteLead(selected)}>{t('deleteLead')}</button>}{selected && selected.status !== 'lost' && !(selected.orders?.length) && <button className="secondary-button accent-button" onClick={() => openOrder(selected)}>{t('convertToOrder')}</button>}<button className="primary-button" disabled={saving} onClick={() => void save()}>{saving ? t('saving') : t('save')}</button></>}>
        {error && <InlineNotice type="error">{error}</InlineNotice>}
        <div className="segmented-control"><button className={form.client_mode === 'existing' ? 'active' : ''} onClick={() => update('client_mode', 'existing')}>{t('existingClient')}</button><button className={form.client_mode === 'contact' ? 'active' : ''} onClick={() => update('client_mode', 'contact')}>{t('newContactOnly')}</button>{!selected && <button className={form.client_mode === 'new' ? 'active' : ''} onClick={() => update('client_mode', 'new')}>{t('createNewClient')}</button>}</div>
        <div className="form-grid">
          {form.client_mode === 'existing' && <label className="field field-span-3"><span>{t('client')} *</span><select value={form.client_id} onChange={(event) => update('client_id', event.target.value)}><option value="">{t('chooseClient')}</option>{clients.map((client) => <option key={client.id} value={client.id}>{clientDisplayName(client)}</option>)}</select></label>}
          {form.client_mode === 'contact' && <label className="field field-span-3"><span>{t('contactLabel')} *</span><input value={form.contact_label} onChange={(event) => update('contact_label', event.target.value)} /></label>}
          {form.client_mode === 'new' && <>
            <label className="field"><span>{t('clientType')} *</span><select value={form.new_client_type} onChange={(event) => update('new_client_type', event.target.value as 'individual' | 'company')}><option value="individual">{t('individual')}</option><option value="company">{t('company')}</option></select></label>
            {form.new_client_type === 'company' ? <label className="field field-span-2"><span>{t('companyName')} *</span><input value={form.new_client_company_name} onChange={(event) => update('new_client_company_name', event.target.value)} /></label> : <><label className="field"><span>{t('firstName')}</span><input value={form.new_client_first_name} onChange={(event) => update('new_client_first_name', event.target.value)} /></label><label className="field"><span>{t('lastName')}</span><input value={form.new_client_last_name} onChange={(event) => update('new_client_last_name', event.target.value)} /></label></>}
            <label className="field"><span>{t('email')}</span><input type="email" value={form.new_client_email} onChange={(event) => update('new_client_email', event.target.value)} /></label>
            <label className="field"><span>{t('phone')}</span><input value={form.new_client_phone} onChange={(event) => update('new_client_phone', event.target.value)} /></label>
            <label className="field"><span>{t('instagram')}</span><input value={form.new_client_instagram} onChange={(event) => update('new_client_instagram', event.target.value)} /></label>
          </>}
          <label className="field"><span>{t('source')} *</span><select value={form.source_id} onChange={(event) => update('source_id', event.target.value)}><option value="">{t('chooseSource')}</option>{sources.map((source) => <option key={source.id} value={source.id}>{sourceName(source, language)}</option>)}</select></label>
          <label className="field"><span>{t('service')} *</span><select value={form.service_id} onChange={(event) => update('service_id', event.target.value)}><option value="">{t('chooseService')}</option>{services.map((service) => <option key={service.id} value={service.id}>{serviceName(service, language)}</option>)}</select></label>
          <label className="field"><span>{t('desiredDate')} *</span><input type="date" value={form.desired_date} onChange={(event) => update('desired_date', event.target.value)} /></label>
          <label className="field"><span>{t('responsible')} *</span><select value={form.responsible_user_id} onChange={(event) => update('responsible_user_id', event.target.value)}><option value="">{t('chooseResponsible')}</option>{profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.full_name || profile.email}</option>)}</select></label>
          <label className="field"><span>{t('status')} *</span><select value={form.status} onChange={(event) => update('status', event.target.value as LeadStatus)}>{leadStatusOptions.map((status) => <option key={status} value={status}>{leadStatusLabel(status, language)}</option>)}</select></label>
          <label className="field"><span>{t('budgetGross')}</span><input inputMode="decimal" value={form.estimated_budget_gross} onChange={(event) => update('estimated_budget_gross', event.target.value)} placeholder="0,00" /></label>
          <label className="field"><span>{t('occasion')}</span><input value={form.occasion} onChange={(event) => update('occasion', event.target.value)} /></label>
          {form.status === 'lost' && <label className="field field-span-2"><span>{t('lostReason')} *</span><input value={form.lost_reason} onChange={(event) => update('lost_reason', event.target.value)} /></label>}
          <label className="field field-span-3"><span>{t('notes')}</span><textarea rows={4} value={form.notes} onChange={(event) => update('notes', event.target.value)} /></label>
        </div>
      </Modal>

      <Modal open={orderOpen} title={t('conversionTitle')} subtitle={selected ? `${selected.number} · ${leadClientName(selected)}` : undefined} onClose={() => setOrderOpen(false)} footer={<><button className="secondary-button" onClick={() => setOrderOpen(false)}>{t('cancel')}</button><button className="primary-button" disabled={orderSaving} onClick={() => void createOrder()}>{orderSaving ? t('saving') : t('createOrder')}</button></>}>
        {error && <InlineNotice type="error">{error}</InlineNotice>}
        <div className="form-grid form-grid-2">
          <label className="field"><span>{t('fulfilmentDateTime')} *</span><input type="datetime-local" value={orderForm.fulfilment_at} onChange={(event) => setOrderForm((current) => ({ ...current, fulfilment_at: event.target.value }))} /></label>
          <label className="field"><span>{t('orderAmountGross')} *</span><input inputMode="decimal" value={orderForm.amount_gross} onChange={(event) => setOrderForm((current) => ({ ...current, amount_gross: event.target.value }))} /></label>
          <label className="field"><span>{t('deliveryMethod')}</span><select value={orderForm.delivery_method} onChange={(event) => setOrderForm((current) => ({ ...current, delivery_method: event.target.value as OrderForm['delivery_method'] }))}><option value="pickup">{t('pickup')}</option><option value="delivery">{t('delivery')}</option><option value="onsite">{t('onsite')}</option></select></label>
          {orderForm.delivery_method === 'delivery' && <label className="field"><span>{t('deliveryAddress')} *</span><input value={orderForm.delivery_address} onChange={(event) => setOrderForm((current) => ({ ...current, delivery_address: event.target.value }))} /></label>}
        </div>
      </Modal>
    </div>
  )
}
